import * as THREE from "three";

export type CosmosWorldNavLevel = "nebula" | "story" | "scene" | "choice";

export const WORLD_GROUP_LERP = 0.05;
export const WORLD_CAMERA_LERP = 0.05;
export const WORLD_DRILL_GROUP_LERP = 0.18;
export const WORLD_ARRIVAL_THRESHOLD = 0.35;

export type PinnedCameraFrame = {
  pos: THREE.Vector3;
  lookAt: THREE.Vector3;
};

export type OverviewFrame = {
  goalPos: [number, number, number];
  goalLookAt: [number, number, number];
};

export type WorldMoverMode = "overview-transition" | "pinned" | "free";

export function isPinnedWorldMoverSettled(
  current: THREE.Vector3,
  target: THREE.Vector3,
): boolean {
  return current.distanceTo(target) < WORLD_ARRIVAL_THRESHOLD;
}

export function vec3FromTuple(tuple: [number, number, number]): THREE.Vector3 {
  return new THREE.Vector3(tuple[0], tuple[1], tuple[2]);
}

export function relativeCameraFrame(
  goalPos: [number, number, number],
  goalLookAt: [number, number, number],
): PinnedCameraFrame {
  const lookAt = vec3FromTuple(goalLookAt);
  const pos = vec3FromTuple(goalPos).sub(lookAt);
  return { pos, lookAt: new THREE.Vector3(0, 0, 0) };
}

export function pinnedCameraForAllLevel(
  navLevel: CosmosWorldNavLevel,
  clusterRadius?: number,
): PinnedCameraFrame {
  const radius = clusterRadius ?? 38;
  if (navLevel === "nebula") {
    const dist = radius * 1.6 + 12;
    return {
      pos: new THREE.Vector3(0, dist * 0.3, dist * 0.85),
      lookAt: new THREE.Vector3(0, 0, 0),
    };
  }
  if (navLevel === "scene" || navLevel === "choice") {
    return {
      pos: new THREE.Vector3(0, 5, 18),
      lookAt: new THREE.Vector3(0, -1.2, 0),
    };
  }
  const dist = Math.max(radius * 0.42 + 10, 20);
  return {
    pos: new THREE.Vector3(0, dist * 0.32, dist * 0.74),
    lookAt: new THREE.Vector3(0, 0, 0),
  };
}

export function pinnedCameraForMineLevel(
  navLevel: CosmosWorldNavLevel,
  clusterRadius?: number,
): PinnedCameraFrame {
  const radius = clusterRadius ?? 38;
  if (navLevel === "nebula") {
    const dist = radius * 1.6 + 12;
    return {
      pos: new THREE.Vector3(0, dist * 0.3, dist * 0.85),
      lookAt: new THREE.Vector3(0, 0, 0),
    };
  }
  if (navLevel === "scene" || navLevel === "choice") {
    return {
      pos: new THREE.Vector3(0, 5, 18),
      lookAt: new THREE.Vector3(0, -1.2, 0),
    };
  }
  const dist = Math.max(radius * 0.42 + 10, 20);
  return {
    pos: new THREE.Vector3(0, dist * 0.32, dist * 0.74),
    lookAt: new THREE.Vector3(0, 0, 0),
  };
}

export function groupOffsetForFocus(focusTarget: THREE.Vector3): THREE.Vector3 {
  return focusTarget.clone().multiplyScalar(-1);
}

export function effectiveWorldPosition(
  localPosition: THREE.Vector3,
  groupOffset: THREE.Vector3,
): THREE.Vector3 {
  return localPosition.clone().add(groupOffset);
}

export function shouldSnapOnDrill(
  prevNavLevel: CosmosWorldNavLevel | undefined,
  navLevel: CosmosWorldNavLevel,
): boolean {
  return prevNavLevel === "nebula" && (navLevel === "story" || navLevel === "scene");
}

export function stepPinnedWorldMover(input: {
  mode: WorldMoverMode;
  focusTarget: THREE.Vector3;
  navLevel: CosmosWorldNavLevel;
  clusterRadius?: number;
  overviewFrame?: OverviewFrame | null;
  drillSnap: boolean;
  skipCameraPin?: boolean;
  currentGroupOffset: THREE.Vector3;
  targetGroupOffset: THREE.Vector3;
  currentCameraPos: THREE.Vector3;
  currentLookAt: THREE.Vector3;
  targetCameraPos: THREE.Vector3;
  targetLookAt: THREE.Vector3;
  pinnedCamera: (level: CosmosWorldNavLevel, radius?: number) => PinnedCameraFrame;
}): { mode: WorldMoverMode; groupLerp: number } {
  const {
    mode,
    focusTarget,
    navLevel,
    clusterRadius,
    overviewFrame,
    drillSnap,
    skipCameraPin = false,
    currentGroupOffset,
    targetGroupOffset,
    currentCameraPos,
    currentLookAt,
    targetCameraPos,
    targetLookAt,
    pinnedCamera,
  } = input;

  const moverMode = mode === "free" ? "pinned" : mode;

  if (moverMode === "overview-transition" && overviewFrame) {
    const overviewRelative = relativeCameraFrame(
      overviewFrame.goalPos,
      overviewFrame.goalLookAt,
    );
    targetGroupOffset.copy(vec3FromTuple(overviewFrame.goalLookAt)).multiplyScalar(-1);
    targetCameraPos.copy(overviewRelative.pos);
    targetLookAt.copy(overviewRelative.lookAt);
  } else {
    targetGroupOffset.copy(groupOffsetForFocus(focusTarget));
    const pinned = pinnedCamera(navLevel, clusterRadius);
    targetCameraPos.copy(pinned.pos);
    targetLookAt.copy(pinned.lookAt);
  }

  const groupLerp =
    drillSnap && moverMode !== "overview-transition"
      ? WORLD_DRILL_GROUP_LERP
      : WORLD_GROUP_LERP;

  if (drillSnap && moverMode !== "overview-transition") {
    currentGroupOffset.copy(targetGroupOffset);
  } else {
    currentGroupOffset.lerp(targetGroupOffset, groupLerp);
  }

  if (!skipCameraPin) {
    if (moverMode === "overview-transition") {
      currentCameraPos.lerp(targetCameraPos, WORLD_CAMERA_LERP);
      currentLookAt.lerp(targetLookAt, WORLD_CAMERA_LERP);
    } else {
      currentCameraPos.copy(targetCameraPos);
      currentLookAt.copy(targetLookAt);
    }
  }

  let nextMode = mode;
  if (
    moverMode === "overview-transition" &&
    isPinnedWorldMoverSettled(currentGroupOffset, targetGroupOffset) &&
    isPinnedWorldMoverSettled(currentCameraPos, targetCameraPos)
  ) {
    nextMode = "pinned";
  }

  return { mode: nextMode, groupLerp };
}
